import { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { Company, Member, Invitation } from '@lendica/utils';
import { getCompanyDetails, useMembershipApi } from '../api';

export interface MembershipDataContext {
    loading: boolean;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    company: Company | null;
    members: Member[];
    setMembers: React.Dispatch<React.SetStateAction<Member[]>>;
    refreshMembers: (force?: boolean, search?: string) => Promise<void>;
    loadMembersPage: (
        page: number,
        pageSize: number,
        force?: boolean,
        search?: string
    ) => Promise<void>;
    prefetchMembersNextPage: (search?: string) => Promise<void>;
    invitations: Invitation[];
    invitationsStatus: 'idle' | 'loading' | 'success' | 'error';
    setInvitations: React.Dispatch<React.SetStateAction<Invitation[]>>;
    refreshInvitations: (force?: boolean, search?: string) => Promise<void>;
    loadInvitationsPage: (
        page: number,
        pageSize: number,
        force?: boolean,
        search?: string
    ) => Promise<void>;
    prefetchInvitationsNextPage: (search?: string) => Promise<void>;
    page: number;
    pageSize: number;
    membersTotal: number;
    invitationsTotal: number;
}

export const DataContext = createContext<MembershipDataContext | undefined>(undefined);

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
    const membershipApi = useMembershipApi();
    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState<Company | null>(null);

    const [members, setMembers] = useState<Member[]>([]);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [membersTotal, setMembersTotal] = useState<number>(0);
    const membersCacheRef = useRef<
        Map<string, { items: Member[]; count: number; fetchedAt: number }>
    >(new Map());

    const membersLoadingRef = useRef(false);
    const loadMembersPage = useCallback(
        async (pageArg: number, pageSizeArg: number, force = false, search?: string) => {
            if (!company?.id) return;
            if (membersLoadingRef.current) return;

            const searchKey = (search || '').trim();
            const key = `${searchKey}|${pageArg}:${pageSizeArg}`;
            const cached = membersCacheRef.current.get(key);
            const isCachedFresh = cached && Date.now() - cached.fetchedAt <= 5 * 60 * 1000;
            if (!force && isCachedFresh) {
                setMembers(cached!.items);
                setPage(pageArg);
                setPageSize(pageSizeArg);
                setMembersTotal(cached!.count);
                return;
            }

            try {
                membersLoadingRef.current = true;
                const data = await membershipApi.getMembers({
                    is_active: true,
                    page: pageArg,
                    page_size: pageSizeArg,
                    search: searchKey || undefined,
                });
                const items = data.results as unknown as Member[];
                const count = data.count ?? items.length;
                membersCacheRef.current.set(key, { items, count, fetchedAt: Date.now() });
                setMembers(items);
                setPage(pageArg);
                setPageSize(pageSizeArg);
                setMembersTotal(count);
            } finally {
                membersLoadingRef.current = false;
            }
        },
        [company?.id, membershipApi]
    );

    const refreshMembers = useCallback(
        async (force = false, search?: string) => {
            if (!company?.id) return;
            await loadMembersPage(page || 1, pageSize || 20, force, search);
        },
        [company?.id, loadMembersPage, page, pageSize]
    );

    const prefetchMembersNextPage = useCallback(
        async (search?: string) => {
            const nextPage = (page || 1) + 1;
            const pageCount = membersTotal && pageSize ? Math.ceil(membersTotal / pageSize) : 0;
            if (membersTotal && nextPage <= pageCount) {
                const searchKey = (search || '').trim();
                const key = `${searchKey}|${nextPage}:${pageSize}`;
                if (!membersCacheRef.current.has(key)) {
                    await loadMembersPage(nextPage, pageSize, false, search);
                }
            }
        },
        [page, pageSize, membersTotal, loadMembersPage]
    );

    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [invitationsStatus, setInvitationsStatus] = useState<
        'idle' | 'loading' | 'success' | 'error'
    >('idle');
    const [invitationsTotal, setInvitationsTotal] = useState<number>(0);
    const [lastInvitationsSearch, setLastInvitationsSearch] = useState<string>('');
    const invitationsCacheRef = useRef<
        Map<string, { items: Invitation[]; count: number; fetchedAt: number }>
    >(new Map());

    const invitationsLoadingRef = useRef(false);
    const loadInvitationsPage = useCallback(
        async (pageArg: number, pageSizeArg: number, force = false, search?: string) => {
            if (!company?.id) return;
            if (invitationsLoadingRef.current) return;

            const searchKey = (search || '').trim();
            setLastInvitationsSearch(searchKey);
            const key = `${searchKey}|${pageArg}:${pageSizeArg}`;
            const cached = invitationsCacheRef.current.get(key);
            const isCachedFresh = cached && Date.now() - cached.fetchedAt <= 5 * 60 * 1000;
            if (!force && isCachedFresh) {
                setInvitations(cached!.items);
                setPage(pageArg);
                setPageSize(pageSizeArg);
                setInvitationsTotal(cached!.count);
                setInvitationsStatus('success');
                return;
            }

            try {
                invitationsLoadingRef.current = true;
                setInvitationsStatus('loading');
                const data = await membershipApi.getInvitations({
                    is_active: false,
                    page: pageArg,
                    page_size: pageSizeArg,
                    search: searchKey || undefined,
                });
                const items = data.results as unknown as Invitation[];
                const count = data.count ?? items.length;
                invitationsCacheRef.current.set(key, { items, count, fetchedAt: Date.now() });
                setInvitations(items);
                setPage(pageArg);
                setPageSize(pageSizeArg);
                setInvitationsTotal(count);
                setInvitationsStatus('success');
            } catch {
                setInvitationsStatus('error');
            } finally {
                invitationsLoadingRef.current = false;
            }
        },
        [company?.id, membershipApi]
    );

    const refreshInvitations = useCallback(
        async (force = false, search?: string) => {
            if (!company?.id) return;
            const effectiveSearch = search !== undefined ? search : lastInvitationsSearch;
            await loadInvitationsPage(page || 1, pageSize || 20, force, effectiveSearch);
        },
        [company?.id, loadInvitationsPage, page, pageSize, lastInvitationsSearch]
    );

    const prefetchInvitationsNextPage = useCallback(
        async (search?: string) => {
            const nextPage = (page || 1) + 1;
            const pageCount =
                invitationsTotal && pageSize ? Math.ceil(invitationsTotal / pageSize) : 0;
            if (invitationsTotal && nextPage <= pageCount) {
                const searchKey = (search || '').trim();
                const key = `${searchKey}|${nextPage}:${pageSize}`;
                if (!invitationsCacheRef.current.has(key)) {
                    await loadInvitationsPage(nextPage, pageSize, false, search);
                }
            }
        },
        [page, pageSize, invitationsTotal, loadInvitationsPage]
    );

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const companyData = await getCompanyDetails();
                setCompany(companyData);
                const memberResp = await membershipApi.getMembers({
                    is_active: true,
                    page: 1,
                    page_size: 20,
                });
                setMembers(memberResp.results);
                setPage(1);
                setPageSize(20);
                setMembersTotal(memberResp.count ?? memberResp.results.length);
                membersCacheRef.current.set(`|1:20`, {
                    items: memberResp.results as unknown as Member[],
                    count: memberResp.count ?? memberResp.results.length,
                    fetchedAt: Date.now(),
                });
                // Background prefetch for invitations using local company id to avoid dependency loops
                const prefetch = async () => {
                    try {
                        const inviteResp = await membershipApi.getInvitations({
                            is_active: false,
                            page: 1,
                            page_size: 20,
                        });
                        setInvitations(inviteResp.results);
                        setPage(1);
                        setPageSize(20);
                        setInvitationsTotal(inviteResp.count ?? inviteResp.results.length);
                        invitationsCacheRef.current.set(`|1:20`, {
                            items: inviteResp.results as unknown as Invitation[],
                            count: inviteResp.count ?? inviteResp.results.length,
                            fetchedAt: Date.now(),
                        });
                        setInvitationsStatus('success');
                    } catch {
                        setInvitationsStatus('error');
                    }
                };
                if ('requestIdleCallback' in window) {
                    (window as any).requestIdleCallback(prefetch);
                } else {
                    setTimeout(prefetch, 0);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [membershipApi]);

    return (
        <DataContext.Provider
            value={{
                loading,
                setLoading,
                company,
                members,
                setMembers,
                refreshMembers,
                loadMembersPage,
                prefetchMembersNextPage,
                page,
                pageSize,
                membersTotal,
                invitations,
                setInvitations,
                refreshInvitations,
                loadInvitationsPage,
                prefetchInvitationsNextPage,
                invitationsStatus,
                invitationsTotal,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};
